import {
  ChangeDetectionStrategy,
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  input,
  model,
  type OnInit,
  signal,
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import type { OdlInputEvent, OdlToggleChip } from '@odilo/ui-components';
import type { Competency } from '../../../models/metadata-response';

let nextPickerId = 0;

/**
 * "Add skills" checkbox plus the toggle chips for the client's competency catalog.
 * When it starts with a selection, only the picked chips are shown behind a "+" button that
 * reveals the rest; otherwise the whole catalog is shown. Unchecking clears the selection.
 */
@Component({
  selector: 'nr-competency-picker',
  imports: [TranslatePipe],
  templateUrl: './competency-picker.html',
  styleUrl: './competency-picker.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class CompetencyPickerComponent implements OnInit {
  private readonly expandedOrder = signal<string[] | undefined>(undefined);

  readonly competencies = input.required<Competency[]>();
  readonly disabled = input(false);
  readonly selected = model<Competency[]>([]);

  readonly enabled = signal(false);

  readonly showAllButtonId = `competency-picker-show-all-${nextPickerId++}`;

  readonly selectedIds = computed(
    () => new Set(this.selected().map((competency) => competency._id)),
  );

  readonly visibleCompetencies = computed(() => {
    const order = this.expandedOrder();
    if (!order) {
      const selectedIds = this.selectedIds();
      return this.competencies().filter((competency) => selectedIds.has(competency._id));
    }
    const byId = new Map(this.competencies().map((competency) => [competency._id, competency]));
    return order
      .map((id) => byId.get(id))
      .filter((competency): competency is Competency => competency !== undefined);
  });

  readonly hasHiddenCompetencies = computed(
    () =>
      this.expandedOrder() === undefined &&
      this.visibleCompetencies().length < this.competencies().length,
  );

  ngOnInit(): void {
    const hasSelection = this.selected().length > 0;
    this.enabled.set(hasSelection);
    this.expandedOrder.set(
      hasSelection ? undefined : this.competencies().map((competency) => competency._id),
    );
  }

  toggleEnabled(event: OdlInputEvent<boolean>): void {
    const checked = event.detail.value;
    this.enabled.set(checked);
    if (!checked) this.selected.set([]);
  }

  onChipInput(id: string, chip: Pick<OdlToggleChip, 'checked'>): void {
    if (this.disabled()) {
      // odl-toggle-chip ignores `disabled` and flips its own `checked` on click; undo it.
      chip.checked = this.selectedIds().has(id);
      return;
    }
    this.toggle(id);
  }

  toggle(id: string): void {
    const current = this.selected();
    if (this.selectedIds().has(id)) {
      this.selected.set(current.filter((competency) => competency._id !== id));
      return;
    }
    const competency = this.competencies().find((candidate) => candidate._id === id);
    if (competency) this.selected.set([...current, competency]);
  }

  showAll(): void {
    const alreadyShown = this.visibleCompetencies().map((competency) => competency._id);
    const rest = this.competencies()
      .map((competency) => competency._id)
      .filter((id) => !alreadyShown.includes(id));
    this.expandedOrder.set([...alreadyShown, ...rest]);
  }
}
